import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSettings } from "@/hooks/useSettings";
import { PLATFORMS, platformIcon } from "@/hooks/useCommunityLinks";

const EMPTY = { name: "", url: "", platform: "other", description: "", enabled: true };
const inputCls = "w-full bg-ink border-3 border-black px-3 py-2 font-mono text-xs text-cream focus:outline-none focus:border-neon";

export default function AdminLinkManager() {
  const qc = useQueryClient();
  const { data: settings } = useSettings();
  const [editing, setEditing] = useState(null); // {id?, ...form}
  const { data: links = [] } = useQuery({ queryKey: ["admin-community-links"], queryFn: async () => (await api.get("/admin/community-links")).data });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-community-links"] }); qc.invalidateQueries({ queryKey: ["community-links"] }); };
  const save = useMutation({
    mutationFn: (l) => l.id ? api.patch(`/admin/community-links/${l.id}`, l) : api.post("/admin/community-links", l),
    onSuccess: () => { toast.success("Saved"); setEditing(null); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/admin/community-links/${id}`), onSuccess: () => { toast.success("Deleted"); invalidate(); } });
  const reorder = useMutation({ mutationFn: (ids) => api.post("/admin/community-links/reorder", { ids }), onSuccess: invalidate });
  const setTg = useMutation({
    mutationFn: (live) => api.patch("/admin/telegram-status", { live }),
    onSuccess: (_r, live) => { toast.success(live ? "Telegram set LIVE" : "Telegram set Coming Soon"); qc.invalidateQueries({ queryKey: ["settings"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= links.length) return;
    const ids = links.map((l) => l.id); [ids[i], ids[j]] = [ids[j], ids[i]]; reorder.mutate(ids);
  };
  const tgLink = links.find((l) => l.platform === "telegram");
  const tgReady = tgLink && tgLink.enabled && (tgLink.url || "").trim();
  const tgLive = !!settings?.telegramLive;

  return (
    <div data-testid="admin-link-manager">
      {/* Telegram launch status */}
      <div className="comic-card p-4 mb-5 bg-ink3">
        <div className="flex items-center gap-2"><Send className="w-5 h-5 text-neon" /><h3 className="font-display font-900 uppercase text-cream text-sm">Telegram Launch Status</h3></div>
        <p className="font-mono text-[11px] text-dim mt-2">
          Status: <span className={tgLive ? "text-neon" : "text-gold"} data-testid="tg-status-value">{tgLive ? "LIVE" : "COMING SOON"}</span>
          {!tgReady && <span className="text-danger"> · add + enable a Telegram link first</span>}
        </p>
        <div className="flex gap-2 mt-3">
          <button disabled={!tgReady || tgLive || setTg.isPending} onClick={() => setTg.mutate(true)} className="btn-neon !py-1.5 !px-3 !text-[11px] disabled:opacity-40" data-testid="tg-set-live">Set LIVE</button>
          <button disabled={!tgLive || setTg.isPending} onClick={() => setTg.mutate(false)} className="btn-ghost !py-1.5 !px-3 !text-[11px] disabled:opacity-40" data-testid="tg-set-comingsoon">Set Coming Soon</button>
        </div>
        <p className="font-mono text-[10px] text-dim mt-2">The live announcement only shows when you set LIVE AND an enabled Telegram link exists. Never auto-announced.</p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="font-mono text-sm text-dim">{links.length} official links</p>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-neon !py-2 !px-3 !text-xs" data-testid="link-add"><Plus className="w-4 h-4" /> Add Link</button>
      </div>

      <div className="space-y-2">
        {links.map((l, i) => {
          const Icon = platformIcon(l.platform);
          return (
            <div key={l.id} className={`comic-card p-2.5 sm:p-3 flex items-center gap-2 ${!l.enabled ? "opacity-60" : ""}`} data-testid={`link-row-${l.id}`}>
              <div className="flex flex-col shrink-0">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-dim hover:text-neon disabled:opacity-30" data-testid={`link-up-${l.id}`}><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === links.length - 1} className="text-dim hover:text-neon disabled:opacity-30" data-testid={`link-down-${l.id}`}><ArrowDown className="w-4 h-4" /></button>
              </div>
              <span className="w-8 h-8 grid place-items-center bg-ink3 border-3 border-black text-neon shrink-0"><Icon className="w-4 h-4" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-display font-900 text-cream text-sm">{l.name}</span>
                  <span className="tag !text-[9px] !px-1.5 !py-0.5 bg-ink3 text-dim">{l.platform}</span>
                  {!l.enabled && <span className="tag !text-[9px] !px-1.5 !py-0.5 bg-gold text-black">off</span>}
                  {!(l.url || "").trim() && <span className="tag !text-[9px] !px-1.5 !py-0.5 bg-danger text-white">no url</span>}
                </div>
                <p className="font-mono text-[10px] text-dim truncate">{l.url || "—"}</p>
              </div>
              <button onClick={() => save.mutate({ ...l, enabled: !l.enabled })} className="text-dim hover:text-neon p-1 shrink-0" data-testid={`link-toggle-${l.id}`} title={l.enabled ? "Disable" : "Enable"}>
                {l.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => setEditing({ ...l })} className="text-dim hover:text-neon p-1 shrink-0" data-testid={`link-edit-${l.id}`}><Pencil className="w-4 h-4" /></button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="text-danger p-1 shrink-0" data-testid={`link-delete-${l.id}`}><Trash2 className="w-4 h-4" /></button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-ink2 border-3 border-black text-cream">
                  <AlertDialogHeader><AlertDialogTitle className="font-display font-900 uppercase text-danger">Delete "{l.name}"?</AlertDialogTitle>
                    <AlertDialogDescription className="font-mono text-xs text-dim">Removes this link from the site. Can't be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-3 border-black bg-ink3 text-cream font-heading uppercase text-xs">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => del.mutate(l.id)} className="border-3 border-black bg-danger text-white font-heading uppercase text-xs" data-testid={`link-delete-confirm-${l.id}`}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-ink2 border-3 border-black text-cream max-w-md" data-testid="link-edit-dialog">
          <DialogHeader><DialogTitle className="font-display font-900 uppercase text-neon">{editing?.id ? "Edit Link" : "Add Link"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Manage an official community link.</DialogDescription>
          {editing && (
            <div className="space-y-3">
              <div><label className="font-mono text-[10px] uppercase text-dim">Platform</label>
                <Select value={editing.platform} onValueChange={(v) => setEditing({ ...editing, platform: v })}>
                  <SelectTrigger className={`${inputCls} mt-1`} data-testid="link-form-platform"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ink2 border-3 border-black text-cream">{PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="font-mono text-[10px] uppercase text-dim">Display Name</label>
                <input className={`${inputCls} mt-1`} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="link-form-name" /></div>
              <div><label className="font-mono text-[10px] uppercase text-dim">URL</label>
                <input className={`${inputCls} mt-1`} placeholder="https://…" value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} data-testid="link-form-url" /></div>
              <div><label className="font-mono text-[10px] uppercase text-dim">Description (optional)</label>
                <input className={`${inputCls} mt-1`} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="link-form-desc" /></div>
              <label className="flex items-center gap-2 font-mono text-xs text-cream cursor-pointer">
                <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} className="w-4 h-4 accent-neon" data-testid="link-form-enabled" /> Enabled (visible on the site)
              </label>
              <button onClick={() => save.mutate(editing)} disabled={!editing.name.trim() || save.isPending} className="btn-neon w-full disabled:opacity-50" data-testid="link-form-save">Save Link</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
